import React, { useId } from 'react';
import { Plus, X, Lock } from 'lucide-react';
import type { FilterCondition, FilterFieldType, FilterGroup, GroupLogic } from '@shared/flow/filter';
import { ME_TOKEN } from '@shared/flow/filter';
import { FILTER_OPERATORS_BY_TYPE, RELATIVE_WINDOW_OPERATORS, RELATIVE_HOUR_OPERATORS, ARRAY_VALUE_OPERATORS, NO_VALUE_OPERATORS, type FilterOperatorOption } from '@shared/flow/filter-operators';
import { valueOptionsOf, type FilterFieldCatalogEntry } from '@shared/flow/filter-fields';
import type { FlowIdentity } from '@shared/flow/identity';
import { FieldPicker, type FieldPickerProps } from './FieldPicker';
import { PersonValuePicker } from './PersonValuePicker';
import { PersonMultiValuePicker } from './PersonMultiValuePicker';
import { MultiValueInput } from './MultiValueInput';
import { ClosedListValuePicker } from './ClosedListValuePicker';
import { AUTOFILL_IGNORE_PROPS } from '../../../lib/autofill';
import { useFilterDraft, type FilterDraft } from './useFilterDraft';
import { useFilterDraftContext } from './FilterDraftContext';
import type { PinnedFilterCondition } from '../types';

/**
 * The Flow-owned filters popover (plan U4, Decision 5) — NOT a rewrite of
 * `mini-apps/_shared/components/TableFilterBuilder.tsx` (Playhouse consumes
 * that directly and must not change). Behavioral contract:
 * `docs/flow/custom-views-prototype.html` `renderFilters()`/`condCard()`
 * (~lines 592-687).
 *
 * Layout: a group renders BOXED (an indented "Filter group" card with its
 * own remove button and an internal "+ Add filter to group") when `gi > 0`,
 * or `group.label` is set (U4, labeled preset groups), or `group.boxed` is
 * `true` (QA round 8, U3 — a group explicitly created via "+ Filter
 * group"); otherwise it renders FLAT (its conditions listed directly, no
 * card). This is a FLAG check, not a positional one, on purpose: a boxed
 * group can end up at index 0 (its flat sibling's last condition was
 * removed — `withRemovedCondition` drops the emptied group) and must keep
 * rendering boxed rather than silently flattening. A saved group from
 * before this round carries no `boxed` key, which keeps its rendering
 * byte-identical. The box header's outer AND/OR toggle renders only when
 * `gi > 0` — with nothing above the box (label- or boxed-only groups at
 * index 0) there is nothing for an outer combinator to combine. A single
 * OUTER `groupLogic` combines every group in the array (mirrors
 * `FlowTable`'s existing `groupLogic`/`onGroupLogicChange` prop pair) —
 * distinct from each `FilterGroup.logic`, which combines that group's OWN
 * conditions and is toggled inline on each non-first condition row.
 *
 * QA-2 REVERSAL (plan V2, docs/flow/custom-views-notes.md "Apply-filters
 * staging"): this is a STAGED editor again. EVERY edit — typed or discrete,
 * field/operator/value/group-logic/add/remove/clear — writes into a local
 * `draftFilters`/`draftGroupLogic` pair only; nothing reaches
 * `onFiltersChange`/`onGroupLogicChange` until "Apply filters" is clicked
 * (disabled while the draft matches the applied props, with an "Unapplied
 * changes" hint while it doesn't). Applying commits both atomically and
 * does NOT close the popover — only an outside click or Escape closes it
 * (both handled by the parent `ResponsivePopover`).
 *
 * GTMOPS-2216 REVERSAL: the draft used to live ENTIRELY here, exactly
 * because `ResponsivePopover` unmounts its children on close, making
 * "closing without Apply" a free discard (see the paragraph this replaces,
 * preserved in git history, and docs/flow/custom-views-notes.md's
 * "Apply-filters staging" section, which called that discard "intentional").
 * Spencer reversed that call: a closed-but-unapplied draft must now survive
 * the close and offer Discard/Edit/Apply through a persistent banner
 * (`FilterDraftBox`, mounted by `FlowTable` next to `ViewDirtyBox`) — which
 * requires the draft to be owned by something that OUTLIVES this popover.
 * The actual staged-state mechanics (seed, reseed-on-external-change, dirty
 * diff, value-shape validation) moved verbatim into `useFilterDraft`
 * (`./useFilterDraft.ts`).
 *
 * Precedence (Phase 4): explicit `draft` prop → enclosing
 * `FilterDraftProvider` (`useFilterDraftContext()`) → self-owned draft. The
 * `draft` prop still wins outright — it's an explicit, deliberate override
 * a caller can reach for regardless of context. `FlowTable` no longer calls
 * `useFilterDraft` in its own render (Phase 4, render isolation — a keystroke
 * into a filter value must not re-render the whole table body); instead it
 * renders `FilterDraftProvider` as the root of its JSX, and this component
 * picks the draft up from context. The `mini-apps/playbooks` direct mounts
 * (`SegmentationConditionsEditor.tsx`, `AudienceRulesStep.tsx`) and this
 * file's own direct-render tests (`FilterPopover.test.tsx`) render this
 * component with no `draft` prop AND outside any `FilterDraftProvider`, so
 * they fall through to the self-owned hook instance unchanged.
 *
 * A value-key ref (`filterDraftKey`/`lastAppliedKeyRef`, ported from
 * eb9e0cca) reseeds the draft whenever the applied props change VALUE while
 * the popover is open — a genuine external reset (view switch, role-default
 * seeding) clobbers any in-progress edit, which is correct; the round-trip
 * where OUR OWN Apply causes `filter`/`groupLogic` to change back down as
 * props is a same-value no-op through this same guard, not a special case.
 *
 * F18 REVERSAL: an earlier design debounced each typed value and flushed the
 * pending one on unmount so closing the popover mid-keystroke never dropped
 * it. Under staging that's no longer right: closing WITHOUT clicking Apply is
 * now the explicit "discard" gesture, so a typed value that never reached
 * Apply is SUPPOSED to be lost on close — dropping trailing keystrokes on
 * close-without-Apply is intentional, not a regression.
 *
 * IF WE EVER WANT LIVE (DEBOUNCED) FILTERING BACK — e.g. the table updating as
 * you type instead of only on Apply — the change is local to this component:
 *   1. Keep `draftFilters`/`draftGroupLogic` as the edit surface.
 *   2. Add a trailing-edge debounce (~250ms) that fires `onFiltersChange`/
 *      `onGroupLogicChange` from a `useEffect` on the draft, instead of only
 *      from the Apply button. Guard it with the same `lastAppliedKeyRef`
 *      value-key so the prop round-trip after each auto-apply is a no-op.
 *   3. Flush the pending debounce on unmount (a ref holding the latest draft +
 *      the change callbacks, invoked in the effect cleanup) so a fast close
 *      doesn't drop the last keystroke — this is the behavior the F18 note
 *      above describes. Reset that ref at the top of the mount effect for
 *      StrictMode safety.
 *   4. Either drop the Apply button or keep it as an explicit "apply now".
 * The prior implementation lived in a `useDebouncedFilterDraft` hook (removed
 * in this PR as dead code once staging landed) — recover it from git history
 * (commit eb9e0cca had the FlowTable-hosted variant) if a reference is useful.
 */
export interface FilterPopoverProps {
  filter: FilterGroup[];
  onFiltersChange: (filter: FilterGroup[]) => void;
  groupLogic: GroupLogic;
  onGroupLogicChange: (logic: GroupLogic) => void;
  fields: FilterFieldCatalogEntry[];
  identity?: FlowIdentity | null;
  /**
   * GTMOPS-2216 — an explicit, externally-owned draft, so it survives THIS
   * component unmounting when the hosting `ResponsivePopover` closes. Takes
   * precedence over an enclosing `FilterDraftProvider` (see the module doc's
   * "Precedence" paragraph). Omit it (every existing direct-render
   * usage/test) and this component either reads one off context or, absent
   * both, instantiates its own via `useFilterDraft`, self-contained exactly
   * as before.
   */
  draft?: FilterDraft;
  /**
   * A mount's always-on, server-enforced conditions (plan 2026-09-25-002
   * D1/U4) — rendered FIRST, above every draft group, as locked rows: an
   * "Always" connector, a lock icon, the condition text, and a `title`
   * tooltip. Never removable, never cleared by "Clear all", never part of
   * `draftFilters`/the staged edit — display-only. Counted in the toolbar's
   * "Filters (N)" by the caller (`FlowTable`'s `activeFilterCount`), not by
   * this component.
   */
  pinned?: readonly PinnedFilterCondition[];
  /** Non-filterable columns shown as inert "filter with …" rows in the field picker (Signals list only, via the table config). */
  fieldAliases?: FieldPickerProps['aliases'];
  /**
   * A caller whose empty filter is not the default (the Home board, where the default is a set of
   * rules). "Clear all" becomes a button with `label` that stages `groups` instead of an empty draft.
   */
  resetTo?: { groups: FilterGroup[]; label: string };
  /** Skip the outer AND/OR toggle on boxed groups, for a caller whose groups never combine. */
  hideGroupLogic?: boolean;
  /** Replaces the sentence shown when the draft has no rules, and shows it even beside pinned rows. */
  emptyCopy?: string;
}

/** One locked, non-removable row shown above the draft filters (D1). */
function PinnedConditionRow({ condition }: { condition: PinnedFilterCondition }) {
  const text = `${condition.label} is ${condition.value}`;
  return (
    <div
      className="flex items-center gap-1.5"
      data-testid="pinned-filter"
      title={condition.tooltip}
      aria-label={`${text} (locked)`}
    >
      <span className="w-9 text-[11px] font-medium text-content-tertiary shrink-0">Always</span>
      <Lock className="w-3.5 h-3.5 text-content-tertiary shrink-0" aria-hidden="true" />
      <div className="flex-1 min-w-0 px-2 py-1 text-[11.5px] text-content-secondary">{text}</div>
      {/* Spacer matching the remove-button column every draft row carries, so
          pinned and draft rows line up even though this row has no ✕. */}
      <div className="w-[26px] shrink-0" />
    </div>
  );
}

// R18 (Signals tab PR 1, review round 1) — this used to be a LOCAL copy
// (`new Set(['is_empty', 'is_not_empty'])`) that drifted out of sync with
// the shared `filter-operators.ts` NO_VALUE_OPERATORS the moment D-L
// promoted `before_today` into it: the Overdue preset's `due_date
// before_today` condition rendered a spurious empty date input here (this
// set said it needed a value) whose edits `condition-to-sql.ts` silently
// ignored (that operator carries no value). Importing the shared set is
// what keeps the two in lockstep going forward — see that module's own
// header comment on why two independent operator lists is exactly this
// drift.

// QA round 8 Codex fixes, F14 — a substring operator needs a free-text
// input no matter what the field's `values` say: "contains" asks for PART of
// a value, which a closed `<select>` cannot express. No existing exported set
// in `filter-operators.ts` carries this exact meaning (`ARRAY_VALUE_OPERATORS`
// is a different pair), so this is its own local set.
const SUBSTRING_OPERATORS = new Set(['contains', 'not_contains']);

let _nextId = 0;
function uid(prefix: string): string {
  return `${prefix}_${++_nextId}_${Date.now()}`;
}

/**
 * The operator options a field actually offers.
 *
 * Codex round-1 finding #2 (client half): the list used to come straight off
 * `FILTER_OPERATORS_BY_TYPE[type]`, ignoring the catalog entry's own
 * `allowedOperators` narrowing. `play_name` declares
 * `['contains', 'equals']`, so the popover was offering `does not contain` —
 * a predicate the Plays evaluator silently dropped for singleton/Gong rows
 * (and which `/api/flow/plays` now rejects outright). The visible filter has
 * to be the filter that runs.
 *
 * Order follows `allowedOperators` itself, NOT the type catalog: the entry
 * lists its operators in the order the rep should see them, and `[0]` is
 * what `firstOperatorFor` hands a new condition. For `play_name` that means
 * the default is `contains` (the search-chip operator), not the text
 * catalog's leading `equals`.
 *
 * A narrowing that matches nothing in the catalog falls back to the full
 * type list rather than rendering an empty `<select>` — an unusable control
 * is worse than a too-wide one, and the route validates regardless.
 */
/**
 * QA round 8 Codex fixes, F5 — the ONE predicate for "does this group render
 * as a bracketed card". Used by the render (`isBoxed`) AND by `addFilter`, so
 * the outer "+ Filter" can never again append into a group that is drawn as
 * a box (a lone `boxed: true` OR group from "+ Filter group" turned
 * "A and B" into "A or B"). Positional: only index 0 can ever be flat.
 */
function groupRendersBoxed(group: FilterGroup, index: number): boolean {
  return index > 0 || Boolean(group.label) || group.boxed === true;
}

function operatorOptionsFor(fieldDef: OperatorScope | undefined): FilterOperatorOption[] {
  const typeCatalog = FILTER_OPERATORS_BY_TYPE[fieldDef?.type ?? 'text'] ?? FILTER_OPERATORS_BY_TYPE.text;
  // U6 — a calendar-DATE field (`granularity: 'day'`) has no clock, so the hour windows are never offered.
  const catalog = fieldDef?.granularity === 'day' ? typeCatalog.filter(o => !RELATIVE_HOUR_OPERATORS.has(o.value)) : typeCatalog;
  const allowed = fieldDef?.allowedOperators;
  if (!allowed) return catalog;
  const byValue = new Map(catalog.map(o => [o.value, o]));
  const narrowed = allowed.flatMap(v => {
    const option = byValue.get(v);
    return option ? [option] : [];
  });
  return narrowed.length > 0 ? narrowed : catalog;
}

/** The `FilterFieldCatalogEntry` fields that decide a condition's operator
 *  set. Structural (not the whole entry) so the two synthetic call sites
 *  below can pass a bare `{ type }` without inventing label/group. */
type OperatorScope = { type: FilterFieldType; allowedOperators?: readonly string[]; granularity?: 'day' };

function firstOperatorFor(fieldDef: OperatorScope | undefined): string {
  return operatorOptionsFor(fieldDef)[0].value;
}

/** `operator` is optional because most callers (field switch, new condition)
 * derive the default purely from `type` — `in_any_of`/`not_in_any_of` (U8),
 * the 4 relative-date operators, and `between` are the operators whose
 * default value shape depends on the OPERATOR rather than the type, since
 * text/picklist/date otherwise default to a plain string. */
function defaultValueFor(type: FilterFieldType, operator?: string): FilterCondition['value'] {
  if (operator != null && ARRAY_VALUE_OPERATORS.has(operator)) return [];
  if (operator != null && RELATIVE_WINDOW_OPERATORS.has(operator)) return '';
  if (operator === 'between') return ['', ''];
  if (type === 'boolean') return 'true';
  if (type === 'person') return ME_TOKEN;
  return '';
}

function newCondition(field: string, fieldDef: OperatorScope | undefined): FilterCondition {
  const type = fieldDef?.type ?? 'text';
  return { id: uid('fc'), field, operator: firstOperatorFor(fieldDef), value: defaultValueFor(type) };
}

/**
 * An operator CHANGE on an existing condition (not a field switch) has no
 * coercion by default, which is fine for every operator pair that shares the
 * plain-string-or-null value shape, but a few operators need an explicit
 * value-shape crossing (keyed by SHAPE, not by the previous operator — this
 * function only receives the next one):
 *  - `in_any_of` / `not_in_any_of` (U8; U8 item 14 for the negated twin):
 *    array shape. Switching INTO either seeds `[]` (`defaultValueFor`'s
 *    branch above) — never carries a stale string value into the array
 *    shape. Switching between the two keeps the array (both are
 *    ARRAY_VALUE_OPERATORS). Switching OUT coerces the array back to its
 *    first element (or `''` if it was empty) — every other value control
 *    expects a plain string/number/null, not an array.
 *  - The 6 relative-window operators (`RELATIVE_WINDOW_OPERATORS` — 4 day, 2
 *    hour): a positive-integer-shaped count. Switching between two relative ops keeps the
 *    day count (relative↔relative). Switching in from anything else reseeds
 *    `''` — a fixed date or a `between` array is not a day count.
 *  - `between`: a 2-element array. Switching in keeps an already-2-element
 *    array (mirrors the `in_any_of`-exit convention of carrying array shape
 *    across compatible operators), else reseeds `['', '']`.
 *  - Switching OUT of a relative op / `between` into `equals`/`gt`/`lt`: an
 *    array carries its first element forward (a `between` start date is a
 *    plausible carry), but a day-count NUMBER never masquerades as a date —
 *    it reseeds `''`.
 */
function coerceValueForOperatorChange(
  type: FilterFieldType,
  currentValue: FilterCondition['value'],
  nextOperator: string,
): FilterCondition['value'] {
  if (ARRAY_VALUE_OPERATORS.has(nextOperator)) {
    return Array.isArray(currentValue) ? currentValue : defaultValueFor(type, nextOperator);
  }

  if (type === 'date' && RELATIVE_WINDOW_OPERATORS.has(nextOperator)) {
    return isPositiveDayCountShaped(currentValue) ? currentValue : '';
  }

  if (type === 'date' && nextOperator === 'between') {
    return Array.isArray(currentValue) && currentValue.length === 2 ? currentValue : ['', ''];
  }

  if (Array.isArray(currentValue)) {
    return currentValue[0] ?? '';
  }

  if (type === 'date' && isPositiveDayCountShaped(currentValue)) {
    // A day-count value (positive-integer-shaped) leaving a relative op for
    // equals/gt/lt is not a date — reseed rather than carry a nonsense value
    // like `90` into a `<input type="date">`.
    return '';
  }

  return currentValue;
}

/** Positive-integer-shaped value check, factored out so both call sites
 * above read as a named condition rather than an inline boolean expression. */
function isPositiveDayCountShaped(value: FilterCondition['value']): boolean {
  return value != null && !Array.isArray(value) && Number.isInteger(Number(value)) && Number(value) > 0;
}

// ── Pure FilterGroup[] transforms (by id, never by index — indices shift
// under concurrent edits) ────────────────────────────────────────────────

function withUpdatedCondition(filter: FilterGroup[], groupId: string, conditionId: string, patch: Partial<FilterCondition>): FilterGroup[] {
  return filter.map(g => (g.id !== groupId ? g : {
    ...g,
    conditions: g.conditions.map(c => (c.id !== conditionId ? c : { ...c, ...patch })),
  }));
}

/** Removing a condition drops its group entirely once empty — matches the
 * prototype's global rule (line 661: `cur.filter = cur.filter.filter(x =>
 * x.conds.length)`), applied uniformly, including group 0. */
function withRemovedCondition(filter: FilterGroup[], groupId: string, conditionId: string): FilterGroup[] {
  return filter
    .map(g => (g.id !== groupId ? g : { ...g, conditions: g.conditions.filter(c => c.id !== conditionId) }))
    .filter(g => g.conditions.length > 0);
}

function withToggledGroupLogic(filter: FilterGroup[], groupId: string): FilterGroup[] {
  return filter.map(g => (g.id !== groupId ? g : { ...g, logic: g.logic === 'AND' ? 'OR' : 'AND' }));
}

function withRemovedGroup(filter: FilterGroup[], groupId: string): FilterGroup[] {
  return filter.filter(g => g.id !== groupId);
}

const SELECT_CLASS =
  'px-1.5 py-1 text-[11.5px] border border-secondary rounded-md bg-white dark:bg-nightshade-800 text-content-primary focus:outline-none focus:ring-1 focus:ring-content-action';

/** Operator select's own width (plan U4's 680px single-line layout:
 * `[And/Or w-9][Field flex-1][operator w-[140px]][value flex-1][✕]`) — kept
 * separate from `SELECT_CLASS` itself rather than baked into that shared
 * constant, since `SELECT_CLASS` is also used (with an appended `w-full`)
 * for the boolean value control, which must fill its own `flex-1` slot, not
 * this fixed 140px. */
const OPERATOR_SELECT_CLASS = `${SELECT_CLASS} w-[140px] shrink-0`;

const VALUE_INPUT_CLASS =
  'w-full px-2 py-1 text-[11.5px] border border-secondary rounded-md bg-white dark:bg-nightshade-800 text-content-primary placeholder:text-content-placeholder focus:outline-none focus:ring-1 focus:ring-content-action';

/**
 * The typed-value input — text/number AND picklist-typed fields (F9:
 * picklist has no enumerated options in the catalog yet, so it's a typed
 * input too). QA-2 REVERSAL: a plain fully-controlled input, no local state
 * and no debounce — `value`/`onCommit` read and write the STAGED draft
 * directly (see the module doc above), so every keystroke is visible
 * immediately in the popover but reaches the applied `filter`/`onFiltersChange`
 * only via "Apply filters". There is nothing left to flush on unmount or
 * blur: an unmount before Apply is the discard gesture (F18 reversal), and
 * because there's no local state to reset, the previous field-switch
 * remount key (`valueControlKey`) is no longer needed either — switching
 * fields already overwrites `condition.value` via `handleFieldChange`.
 */
function FreeTextValueInput({
  value, numeric, onCommit, suggestions,
}: { value: string; numeric?: boolean; onCommit: (v: string) => void; suggestions?: readonly string[] }) {
  // QA round 8 Codex fixes, F12/F14 — the field's `values` (a custom-value
  // field, or a substring operator on an enumerated field) are offered as
  // native `<datalist>` suggestions rather than a closed `<select>`: the rep
  // can still type anything, including a value the hand-maintained list is
  // missing.
  const listId = useId();
  const hasSuggestions = suggestions && suggestions.length > 0;
  return (
    <>
      <input
        {...AUTOFILL_IGNORE_PROPS}
        type={numeric ? 'number' : 'text'}
        value={value}
        onChange={e => onCommit(e.target.value)}
        placeholder="value"
        className={VALUE_INPUT_CLASS}
        list={hasSuggestions ? listId : undefined}
      />
      {hasSuggestions ? (
        <datalist id={listId}>
          {suggestions.map(s => <option key={s} value={s} />)}
        </datalist>
      ) : null}
    </>
  );
}

interface ConditionRowProps {
  condition: FilterCondition;
  index: number;
  groupLogic: GroupLogic;
  fields: FilterFieldCatalogEntry[];
  identity?: FlowIdentity | null;
  fieldAliases?: FieldPickerProps['aliases'];
  onToggleGroupLogic: () => void;
  onFieldChange: (key: string) => void;
  onOperatorChange: (op: string) => void;
  onValueChange: (value: FilterCondition['value']) => void;
  onRemove: () => void;
}

function ConditionRow({
  condition, index, groupLogic, fields, identity, fieldAliases,
  onToggleGroupLogic, onFieldChange, onOperatorChange, onValueChange, onRemove,
}: ConditionRowProps) {
  const fieldDef = fields.find(f => f.key === condition.field);
  const type: FilterFieldType = fieldDef?.type ?? 'text';
  const operators = operatorOptionsFor(fieldDef);
  const needsValue = !NO_VALUE_OPERATORS.has(condition.operator);

  // R5 — a condition whose `field` key isn't in the catalog (e.g. a legacy
  // saved view referencing `tracker_status`, removed from the picker in U7
  // but still mapped server-side for backward read compatibility) must never
  // fall through to the normal FieldPicker/operator-select/value-input
  // chain: that chain assumes `fieldDef` exists (it doesn't — the field
  // "misrepresents" as a plain text field with a bogus default operator) and
  // any edit through it would silently coerce the stored value/operator into
  // a shape the field was never validated against. Render read-only instead
  // — only the ✕ remove button stays functional.
  if (!fieldDef) {
    return (
      <div className="flex items-center gap-1.5">
        {index === 0 ? (
          <span className="w-9 text-[11px] font-medium text-content-tertiary shrink-0">Where</span>
        ) : (
          <button
            type="button"
            onClick={onToggleGroupLogic}
            className="w-9 text-[11px] font-semibold text-content-action shrink-0 text-left"
            title="Toggle AND/OR"
          >
            {groupLogic === 'AND' ? 'And' : 'Or'}
          </button>
        )}
        <div className="flex-1 min-w-0 px-2 py-1 text-[11.5px] text-content-tertiary italic">
          Legacy field: {condition.field}
        </div>
        <button
          type="button"
          onClick={onRemove}
          title="Remove"
          className="p-1 text-content-tertiary hover:text-content-danger rounded shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      {index === 0 ? (
        <span className="w-9 text-[11px] font-medium text-content-tertiary shrink-0">Where</span>
      ) : (
        <button
          type="button"
          onClick={onToggleGroupLogic}
          className="w-9 text-[11px] font-semibold text-content-action shrink-0 text-left"
          title="Toggle AND/OR"
        >
          {groupLogic === 'AND' ? 'And' : 'Or'}
        </button>
      )}
      <div className="flex-1 min-w-0">
        <FieldPicker fields={fields} value={condition.field} onSelect={onFieldChange} aliases={fieldAliases} />
      </div>
      <select
        value={condition.operator}
        onChange={e => onOperatorChange(e.target.value)}
        className={OPERATOR_SELECT_CLASS}
      >
        {operators.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
      </select>
      {needsValue ? (
        <div className="flex-1 min-w-0">
          {ARRAY_VALUE_OPERATORS.has(condition.operator) && type === 'person' ? (
            // U9 — person's own multi-select mode, checked BEFORE the
            // generic array-operator branch below: operator can be
            // `in_any_of`/`not_in_any_of` (U8, item 14) AND type can be
            // `person` simultaneously, and person's multi-select needs its
            // own component (scoped candidate list, never offers `{me}`),
            // not the generic free-entry chips.
            <PersonMultiValuePicker
              value={Array.isArray(condition.value) ? condition.value : []}
              scope={fieldDef?.peopleScope}
              onChange={onValueChange}
            />
          ) : ARRAY_VALUE_OPERATORS.has(condition.operator) && fieldDef?.valueInput === 'closed-list' && !fieldDef.allowCustomValue ? (
            // Opt-in closed list (the Home board's picklists): every option is a row to tick, so a
            // rep never has to guess what to type. Same stored array as the chips input below.
            <ClosedListValuePicker
              value={Array.isArray(condition.value) ? condition.value : []}
              options={valueOptionsOf(fieldDef)}
              ariaLabel={`${fieldDef.label} ${operators.find(op => op.value === condition.operator)?.label ?? ''}`.trim()}
              onChange={onValueChange}
            />
          ) : ARRAY_VALUE_OPERATORS.has(condition.operator) ? (
            // U8 — free-entry chips, valid for text/picklist, for BOTH array
            // operators (`in_any_of` / `not_in_any_of`, item 14 — the same
            // chip control, just negated at the SQL layer). `options` (U7)
            // turns the free-entry suggestions into the field's enumerated
            // `values` when present (e.g. the tracker's Status buckets) —
            // still chips, but picked from a closed list.
            <MultiValueInput
              value={Array.isArray(condition.value) ? condition.value : []}
              options={fieldDef ? valueOptionsOf(fieldDef) : undefined}
              onChange={onValueChange}
            />
          ) : type === 'boolean' ? (
            <select
              value={condition.value != null ? String(condition.value) : 'true'}
              onChange={e => onValueChange(e.target.value)}
              className={`${SELECT_CLASS} w-full`}
            >
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          ) : type === 'date' && RELATIVE_WINDOW_OPERATORS.has(condition.operator) ? (
            // Relative-window operators (in_last_days / not_in_last_days /
            // in_next_days / not_in_next_days, and — U6 — in_last_hours /
            // not_in_last_hours) — a count of a unit, not a date. ALL SIX
            // route through this one check (the launcher's known 3-of-4
            // wiring bug is the anti-pattern this avoids). The unit and the
            // cap follow the operator: days up to 100 years, hours up to 30
            // days (`validateOperatorValueShape` is the real gate).
            (() => {
              const unit = RELATIVE_HOUR_OPERATORS.has(condition.operator) ? 'hours' : 'days';
              return (
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min="1"
                    max={unit === 'hours' ? '720' : '36500'}
                    {...AUTOFILL_IGNORE_PROPS}
                    aria-label={`Number of ${unit}`}
                    value={condition.value != null ? String(condition.value) : ''}
                    onChange={e => onValueChange(e.target.value)}
                    className={VALUE_INPUT_CLASS}
                  />
                  <span className="text-[11.5px] text-content-tertiary shrink-0">{unit}</span>
                </div>
              );
            })()
          ) : type === 'date' && condition.operator === 'between' ? (
            // `min-w-0` on both inputs: a native date input has an intrinsic
            // min-width, and two of them side-by-side overflow the flex-1
            // value column — sliding the second one's calendar picker under
            // the row's ✕ button (QA find). Letting them shrink keeps the
            // picker clickable; Chromium compresses the date segments to fit.
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                {...AUTOFILL_IGNORE_PROPS}
                aria-label="Range start date"
                value={Array.isArray(condition.value) ? (condition.value[0] ?? '') : ''}
                onChange={e => onValueChange([e.target.value, Array.isArray(condition.value) ? (condition.value[1] ?? '') : ''])}
                className={`${VALUE_INPUT_CLASS} min-w-0`}
              />
              <input
                type="date"
                {...AUTOFILL_IGNORE_PROPS}
                aria-label="Range end date"
                value={Array.isArray(condition.value) ? (condition.value[1] ?? '') : ''}
                onChange={e => onValueChange([Array.isArray(condition.value) ? (condition.value[0] ?? '') : '', e.target.value])}
                className={`${VALUE_INPUT_CLASS} min-w-0`}
              />
            </div>
          ) : type === 'date' ? (
            <input
              type="date"
              {...AUTOFILL_IGNORE_PROPS}
              value={condition.value != null ? String(condition.value) : ''}
              onChange={e => onValueChange(e.target.value)}
              className={VALUE_INPUT_CLASS}
            />
          ) : type === 'person' ? (
            <PersonValuePicker
              value={condition.value}
              identity={identity}
              scope={fieldDef?.peopleScope}
              onSelect={onValueChange}
            />
          ) : fieldDef?.values && !fieldDef.allowCustomValue && !SUBSTRING_OPERATORS.has(condition.operator) ? (
            // U7 — a picklist field with enumerated `values` (the tracker's
            // Status buckets) gets a real select, discrete-commit like
            // boolean/date above, replacing free-typing for this field: a
            // rep filtering "status is Due" must pick from the closed list,
            // not retype a label that has to match a derived bucket exactly.
            //
            // QA round 8 Codex fixes, F12/F14 — this select is now skipped
            // in two more cases: a field flagged `allowCustomValue` (its
            // `values` are suggestions, not a closed set — F12: a saved
            // `cm_status equals Invited` must render, not disappear behind a
            // stuck "Select…"), and a SUBSTRING operator on ANY enumerated
            // field (F14: `contains`/`not_contains` need a free-text partial
            // match, which a closed select cannot express). Both fall
            // through to the free-text branch below, which offers the same
            // `values` as `<datalist>` suggestions instead.
            <select
              value={condition.value != null ? String(condition.value) : ''}
              onChange={e => onValueChange(e.target.value)}
              className={`${SELECT_CLASS} w-full`}
            >
              <option value="">Select…</option>
              {/* A1 — `values` may be labeled options (`{ value, label }`):
                  the option STORES the stable id and DISPLAYS the human
                  name. `valueOptionsOf` normalizes the plain-string form to
                  the same shape, so both render through this one branch. */}
              {valueOptionsOf(fieldDef).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ) : (
            // text, number, AND picklist with no enumerated `values` all
            // share the one plain typed-value control (F9: picklist-typed
            // used to commit per keystroke via an uncontrolled input; it now
            // writes straight into the staged draft, same as free text — no
            // separate debounce flavor, and QA-2 removed the debounce
            // itself, see FreeTextValueInput's doc comment). Also reached by
            // an `allowCustomValue` field or a substring operator on an
            // enumerated field (F12/F14, see the select branch above) — in
            // either case the field's own `values` (if any) are still
            // offered, as suggestions rather than a closed list.
            <FreeTextValueInput
              value={condition.value != null ? String(condition.value) : ''}
              numeric={type === 'number'}
              onCommit={onValueChange}
              suggestions={fieldDef?.values ? valueOptionsOf(fieldDef).map(o => o.value) : undefined}
            />
          )}
        </div>
      ) : (
        // NO_VALUE_OPERATORS (is_empty / is_not_empty): a plain spacer holds
        // the value column's width so every row's [✕] lands in the same
        // place regardless of which operator is selected.
        <div className="flex-1 min-w-0" />
      )}
      <button
        type="button"
        onClick={onRemove}
        title="Remove"
        className="p-1 text-content-tertiary hover:text-content-danger rounded shrink-0"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export function FilterPopover({ filter, onFiltersChange, groupLogic, onGroupLogicChange, fields, identity, draft: externalDraft, pinned, fieldAliases, resetTo, hideGroupLogic, emptyCopy }: FilterPopoverProps) {
  const defaultFieldKey = fields[0]?.key ?? '';
  const defaultFieldDef = fields[0];

  // ── Staged draft (QA-2 reversal; GTMOPS-2216 / Phase 4 — see the module doc
  // above for the precedence rule). Always instantiate the self-contained
  // hook and always read context (rules-of-hooks: neither can be conditional
  // on `externalDraft`), then resolve explicit prop → context → self-owned.
  // The unused internal instance costs a little state nobody reads — cheap,
  // and it keeps every existing direct-render usage/test (no `draft` prop,
  // no enclosing provider) working unchanged.
  const ownDraft = useFilterDraft({ filter, onFiltersChange, groupLogic, onGroupLogicChange, fields });
  const contextDraft = useFilterDraftContext();
  const {
    draftFilters, draftGroupLogic, setDraftFilters, setDraftGroupLogic,
    isDirty: filtersDirty, validationError: draftValidationError, apply: applyFilters,
  } = externalDraft ?? contextDraft ?? ownDraft;

  const addFilter = () => {
    const cond = newCondition(defaultFieldKey, defaultFieldDef);
    setDraftFilters(prev => {
      if (prev.length === 0) return [{ id: uid('fg'), logic: 'AND', conditions: [cond] }];
      // Group 0 always renders FIRST (flat, unboxed — see the module doc),
      // so once a bracketed "Filter group" already exists below it, adding
      // to group 0 here would land the new condition ABOVE those groups —
      // the opposite end of the popover from the "+ Filter" button that was
      // just clicked (Spencer bug report, 2026-08-13). With no bracketed
      // group yet, appending to group 0 already lands correctly at the
      // bottom (there's nothing below it to jump above), so that path is
      // untouched. Once one exists, append a NEW single-condition group at
      // the array's end instead — visually at the bottom, where the click
      // happened — rather than reordering anything already on screen.
      //
      // QA round 8 Codex fixes, F5 — "once one exists" used to mean
      // `prev.length === 1`, which does not distinguish a lone FLAT group
      // (this original 2026-08-13 case) from a lone group that itself
      // renders BOXED (`boxed: true`, from "+ Filter group" on an empty
      // draft): appending into a boxed group's own conditions turns
      // "A AND B" into "A OR B" inside the bracket. `groupRendersBoxed` is
      // the single source of truth for that distinction (shared with
      // `isBoxed` below) — only a lone FLAT group 0 still gets the
      // append-in-place treatment.
      if (prev.length === 1 && !groupRendersBoxed(prev[0], 0)) {
        return prev.map((g, i) => (i === 0 ? { ...g, conditions: [...g.conditions, cond] } : g));
      }
      return [...prev, { id: uid('fg'), logic: 'AND', conditions: [cond] }];
    });
  };

  const addFilterGroup = () => {
    // QA round 8 (U3) — always append exactly ONE new group, flagged
    // `boxed: true` so it keeps rendering as a bracketed card even if it is
    // later promoted to index 0 (e.g. group 0's only condition gets
    // removed). No flat-condition seeding on an empty draft: the old
    // `base` fallback existed only to give the new group something to sit
    // under, which is what made the positional `gi === 0` rendering rule
    // fragile in the first place.
    setDraftFilters(prev => [...prev, { id: uid('fg'), logic: 'OR', conditions: [newCondition(defaultFieldKey, defaultFieldDef)], boxed: true }]);
  };

  const addInGroup = (groupId: string) => {
    setDraftFilters(prev => prev.map(g => (g.id === groupId
      ? { ...g, conditions: [...g.conditions, newCondition(defaultFieldKey, defaultFieldDef)] }
      : g)));
  };

  const clearAll = () => setDraftFilters(resetTo ? resetTo.groups : []);

  const updateCondition = (groupId: string, conditionId: string, patch: Partial<FilterCondition>) => {
    setDraftFilters(prev => withUpdatedCondition(prev, groupId, conditionId, patch));
  };

  const handleFieldChange = (groupId: string, conditionId: string, key: string) => {
    const nextFieldDef = fields.find(f => f.key === key);
    const type = nextFieldDef?.type ?? 'text';
    updateCondition(groupId, conditionId, { field: key, operator: firstOperatorFor(nextFieldDef), value: defaultValueFor(type) });
  };

  const removeCondition = (groupId: string, conditionId: string) => {
    setDraftFilters(prev => withRemovedCondition(prev, groupId, conditionId));
  };

  const toggleGroupInnerLogic = (groupId: string) => {
    setDraftFilters(prev => withToggledGroupLogic(prev, groupId));
  };

  const removeGroup = (groupId: string) => {
    setDraftFilters(prev => withRemovedGroup(prev, groupId));
  };

  const conditionRowProps = (group: FilterGroup, cond: FilterCondition, ci: number) => ({
    condition: cond,
    index: ci,
    groupLogic: group.logic,
    fields,
    identity,
    fieldAliases,
    onToggleGroupLogic: () => toggleGroupInnerLogic(group.id),
    onFieldChange: (key: string) => handleFieldChange(group.id, cond.id, key),
    onOperatorChange: (op: string) => {
      // U8 — the array shape crossing (see `coerceValueForOperatorChange`'s
      // doc comment): read `cond`'s field type from THIS render's `fields`
      // prop, not a stale closure — `fields` doesn't change mid-popover.
      const type = fields.find(f => f.key === cond.field)?.type ?? 'text';
      updateCondition(group.id, cond.id, { operator: op, value: coerceValueForOperatorChange(type, cond.value, op) });
    },
    onValueChange: (v: FilterCondition['value']) => updateCondition(group.id, cond.id, { value: v }),
    onRemove: () => removeCondition(group.id, cond.id),
  });

  return (
    <div className="w-[680px] max-w-full p-2 space-y-2">
      {pinned && pinned.length > 0 && (
        <div className="space-y-1.5">
          {pinned.map(condition => (
            <PinnedConditionRow key={`${condition.label}:${condition.value}`} condition={condition} />
          ))}
        </div>
      )}

      {draftFilters.length === 0 && (emptyCopy !== undefined || !pinned || pinned.length === 0) && (
        <p className="text-[12px] text-content-tertiary px-1 py-1.5">
          {emptyCopy ?? 'No filters — showing every record you can see.'}
        </p>
      )}

      {draftFilters.map((group, gi) => {
        // QA round 8 (U3) — boxed-vs-flat is a group FLAG, not a positional
        // index. `gi > 0` still boxes every non-first group (unchanged);
        // `group.label` still boxes a labeled group 0 (U4, unchanged); and
        // `group.boxed` now boxes a group explicitly created via "+ Filter
        // group" even if it is later promoted to index 0 (its flat
        // sibling's last condition got removed, see `withRemovedCondition`).
        // A saved group from before this round has no `boxed` key and
        // keeps rendering exactly as it did (byte-identical: group 0 flat,
        // groups 1+ boxed). QA round 8 Codex fixes, F5 — this predicate is
        // now shared with `addFilter` as `groupRendersBoxed`, so the render
        // and the outer "+ Filter" button can never again disagree about
        // whether this group is drawn as a box.
        const isBoxed = groupRendersBoxed(group, gi);
        return (
        <React.Fragment key={group.id}>
          {!isBoxed ? (
            <div className="space-y-1.5">
              {group.conditions.map((cond, ci) => <ConditionRow key={cond.id} {...conditionRowProps(group, cond, ci)} />)}
            </div>
          ) : (
            <div className="rounded-lg border border-secondary bg-bg-secondary/40 p-2 space-y-1.5">
              <div className="flex items-center gap-1.5">
                {gi > 0 && !hideGroupLogic && (
                  <div className="flex rounded-md overflow-hidden border border-secondary">
                    {(['AND', 'OR'] as const).map(l => (
                      <button
                        key={l}
                        type="button"
                        onClick={() => setDraftGroupLogic(l)}
                        className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                          draftGroupLogic === l
                            ? 'bg-content-action text-white dark:bg-blueberry-500'
                            : 'bg-white dark:bg-nightshade-800 text-content-tertiary dark:text-nightshade-500'
                        }`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                )}
                <span className="text-[11px] font-medium text-content-secondary flex-1">{group.label ?? 'Filter group'}</span>
                <button
                  type="button"
                  onClick={() => removeGroup(group.id)}
                  title="Remove group"
                  className="p-0.5 text-content-tertiary hover:text-content-danger rounded"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="space-y-1.5">
                {group.conditions.map((cond, ci) => <ConditionRow key={cond.id} {...conditionRowProps(group, cond, ci)} />)}
              </div>
              <button
                type="button"
                onClick={() => addInGroup(group.id)}
                className="flex items-center gap-1 px-1.5 py-1 text-[11.5px] text-content-tertiary hover:text-content-action rounded-md"
              >
                <Plus className="w-3 h-3" /> Add filter to group
              </button>
            </div>
          )}
        </React.Fragment>
        );
      })}

      <div className="flex items-center gap-3 pt-1 border-t border-secondary">
        <button type="button" onClick={addFilter} className="flex items-center gap-1 text-[11.5px] text-content-secondary hover:text-content-action">
          <Plus className="w-3 h-3" /> Filter
        </button>
        <button type="button" onClick={addFilterGroup} className="flex items-center gap-1 text-[11.5px] text-content-secondary hover:text-content-action">
          <Plus className="w-3 h-3" /> Filter group
        </button>
        <button type="button" onClick={clearAll} className="ml-auto flex items-center gap-1 text-[11.5px] text-content-tertiary hover:text-content-danger">
          <X className="w-3 h-3" /> {resetTo?.label ?? 'Clear all'}
        </button>
      </div>

      {/* QA-2 Apply-filters staging (see module doc) — disabled while the
          draft matches the applied filter/groupLogic props, primary +
          "Unapplied changes" hint while it doesn't. Clicking Apply commits
          both atomically; the popover itself never closes from here. */}
      <div className="flex items-center gap-2 pt-1 border-t border-secondary">
        <div className="flex-1" />
        {draftValidationError ? (
          <span className="text-[11px] text-content-tertiary">{draftValidationError}</span>
        ) : filtersDirty && (
          <span className="text-[11px] text-content-tertiary">Unapplied changes</span>
        )}
        <button
          type="button"
          onClick={applyFilters}
          disabled={!filtersDirty || !!draftValidationError}
          className="text-[11.5px] font-semibold px-3 py-1.5 rounded-md bg-content-action text-white hover:bg-content-action-hover dark:bg-blueberry-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Apply filters
        </button>
      </div>
    </div>
  );
}
